import { type JSX, onMount } from 'solid-js';
import { ViewThumbnail } from '../ViewThumbnail/ViewThumbnail';
import { IFeatureConfig } from '../../types/issue.types';
import type { MayaReservedViewData } from '@trimble/tc-issue-management-providers';
import { translate } from '@fulcrum/lib';

const OPEN_IN_360_VIEWER_KEY = 'TC.Topic.Open360Viewer';
const getOpenIn360ViewerLabel = (): string => translate(OPEN_IN_360_VIEWER_KEY);

export interface IViewpoint {
  // eslint-disable-next-line @typescript-eslint/naming-convention -- view_id is from API response format
  view_id: string;
  guid: string;
  name: string;
  thumbnail?: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- created_at is from API response format
  created_at: string;
  author: string;
}

export interface IUser {
  tiduuid: string;
  firstName?: string;
  lastName?: string;
  email: string;
}

interface IProps {
  onClose(): void;
  viewpointList: IViewpoint[];
  previewIndex: number;
  viewRedirection(attachmentId: string, type: string): void;
  previewMode: 'VIEW' | 'SLIDEVIEW';
  projectId: string;
  issueId: string;
  embedIn3D: boolean;
  deleteViewpoint(viewpointId: string): Promise<void>;
  setViewpointIndex(index: number): void;
  accessToken?: string;
  featureConfig?: IFeatureConfig;
  reservedViewDataMap?: Record<string, MayaReservedViewData>;
  open360Viewer?(viewId: string): boolean | Promise<boolean>;
}

export function RightPanelOverlay(props: IProps): JSX.Element {
  onMount(() => {
    // Component is now focused only on viewpoint previews
  });

  const selectViewpoint = async (): Promise<void> => {
    // TODO: Implement viewpoint selection logic
    console.log('Select viewpoint clicked');
  };

  const onDeleteViewpoint = async (): Promise<void> => {
    try {
      const selectedViewpoint = props.viewpointList[props.previewIndex];
      await props.deleteViewpoint(selectedViewpoint.guid);
    } catch (e) {
      console.error(e);
    }
    props.onClose();
  };

  const selectedViewpoint = (): IViewpoint =>
    props.viewpointList[props.previewIndex];
  const selectedViewpointId = (): string =>
    props.viewpointList[props.previewIndex].view_id;
  const makeViewpoint = (): string | undefined =>
    selectedViewpoint()?.thumbnail;
  const hasMayaReservedViewData = (): boolean =>
    Boolean(props.reservedViewDataMap?.[selectedViewpointId()]);
  const shouldShow360ViewerLabel = (): boolean =>
    hasMayaReservedViewData() &&
    props.featureConfig?.mayaReservedViewRouting?.redirectToProjectSight !==
      false;
  const onViewerButtonClick = async (): Promise<void> => {
    const viewId = selectedViewpointId();
    if (hasMayaReservedViewData() && (await props.open360Viewer?.(viewId))) {
      return;
    }

    if (props.embedIn3D) {
      await selectViewpoint();
      return;
    }

    props.viewRedirection(viewId, 'VIEW');
  };

  return (
    <div class="topics-overlay z-modal-backdrop">
      <header>
        {props.previewMode !== 'SLIDEVIEW' && (
          <div class="panel-title">
            <div>
              <button class="button secondary" onClick={onViewerButtonClick}>
                {shouldShow360ViewerLabel()
                  ? getOpenIn360ViewerLabel()
                  : (props.featureConfig?.customText?.viewIn3DViewerLabel ??
                    translate('TC.FileViewer.Worldviewer'))}
              </button>
            </div>
            <div class="row-distribute-from-end">
              <button
                class="button white hollow"
                onClick={(): void => props.onClose()}
              >
                <i class="icon-font tc-icon-content-close"></i>
              </button>
            </div>
          </div>
        )}
      </header>
      <div class="moduleFluid px-2">
        <div class="topic-thumbnail-large">
          <ViewThumbnail
            view={{
              id: selectedViewpoint()?.guid || '',
              name: selectedViewpoint()?.name || '',
              thumbnail: makeViewpoint(),
              createdOn: selectedViewpoint()?.created_at || '',
              createdBy: selectedViewpoint()?.author
                ? {
                    firstName: selectedViewpoint()?.author.split(' ')[0] || '',
                    lastName: selectedViewpoint()?.author.split(' ')[1] || '',
                  }
                : undefined,
            }}
            class="topic-thumbnail-large"
            accessToken={props.accessToken}
          />
        </div>
        <div class="row-distribute-from-end">
          <div class="d-flex">
            <button
              class="button white hollow small"
              onClick={(): Promise<void> => onDeleteViewpoint()}
            >
              {translate('TC.Common.Delete')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
